import { useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import { apiClient } from '../services/apiClient'

export default function EmailVerificationRequiredPage() {
  const { user, logout } = useAuth()
  const [resendLoading, setResendLoading] = useState(false)
  const [resendMessage, setResendMessage] = useState('')
  const [resendSuccess, setResendSuccess] = useState(false)

  const handleResendVerification = async () => {
    if (!user?.email) {
      setResendMessage('Unable to resend verification: user email not available')
      setResendSuccess(false)
      return
    }

    setResendLoading(true)
    setResendMessage('')
    setResendSuccess(false)
    
    try {
      const response = await apiClient.resendEmailVerification(user.email)
      setResendMessage(response.message || 'Verification email sent to your inbox!')
      setResendSuccess(true)
    } catch (error) {
      setResendMessage(error instanceof Error ? error.message : 'Failed to resend verification email')
      setResendSuccess(false)
    } finally {
      setResendLoading(false)
    }
  }

  const handleLogout = async () => {
    await logout()
  }

  return (
    <div className="min-h-screen bg-firedup-dark flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-white mb-2">
            Email Verification Required
          </h2>
          <div className="w-16 h-1 bg-firedup-orange mx-auto mb-8"></div>
        </div>

        <div className="bg-white rounded-lg shadow-xl p-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Please Verify Your Email
            </h3>
            
            <p className="text-gray-600 mb-4">
              To access your dashboard and personal features, you need to verify your email address first.
            </p>
            
            {user && (
              <div className="bg-gray-50 rounded-lg p-4 mb-6 border-l-4 border-firedup-orange">
                <p className="text-sm text-gray-700 mb-2">
                  <strong>Verification email sent to:</strong>
                </p>
                <p className="text-lg font-mono text-gray-900 bg-white px-3 py-2 rounded border">
                  {user.email}
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  ⚠️ Is this email address correct? If not, sign out and register again.
                </p>
              </div>
            )}
            
            <p className="text-sm text-gray-600 mb-6">
              We've sent a verification link to your email address. Please check your inbox and click the link to verify your account.
            </p>

            <div className="space-y-4">
              <button
                onClick={handleResendVerification}
                disabled={resendLoading}
                className="w-full bg-firedup-orange text-white py-2 px-4 rounded-md hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
              >
                {resendLoading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </span>
                ) : 'Resend Verification Email'}
              </button>
              
              {resendMessage && (
                <div className={`p-3 rounded-md text-sm ${
                  resendSuccess 
                    ? 'bg-green-50 text-green-700 border border-green-200' 
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  {resendMessage}
                </div>
              )}
              
              <button
                onClick={handleLogout}
                className="w-full bg-gray-100 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-200 transition duration-200"
              >
                Sign Out
              </button>
            </div>
            
            <div className="mt-6 text-center">
              <details className="text-left">
                <summary className="text-sm text-firedup-orange cursor-pointer hover:text-orange-600 mb-3">
                  📧 Didn't receive the email? Click here for help
                </summary>
                <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-700 space-y-2">
                  <p><strong>✅ Check these common issues:</strong></p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li>Look in your <strong>spam/junk folder</strong></li>
                    <li>Check if <strong>{user?.email}</strong> is correct</li>
                    <li>Ensure your email provider allows emails from <strong>@firedupgames.com</strong></li>
                    <li>Wait a few minutes - emails can sometimes be delayed</li>
                  </ul>
                  
                  <p className="pt-2"><strong>🔄 Still no email?</strong></p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li>Try the "Resend" button above (limit: once per 5 minutes)</li>
                    <li>Sign out and register again with a different email address</li>
                    <li>Contact support if problems persist</li>
                  </ul>
                  
                  <div className="pt-3 border-t border-gray-200">
                    <p className="text-xs text-gray-500">
                      <strong>Email incorrect?</strong> Sign out and register with the correct email address.
                      <br />
                      <strong>Need help?</strong> Contact support at <a href="mailto:support@firedupgames.com" className="text-firedup-orange hover:underline">support@firedupgames.com</a>
                    </p>
                  </div>
                </div>
              </details>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
